/**
 * Redirects to MFA enrollment when the backend has refused a request with
 * `403 {"error": "mfa_enrollment_required"}`.
 *
 * WHY THIS IS SEPARATE FROM ProtectedRoute
 * ----------------------------------------
 * ProtectedRoute requires a Firebase session and sends anyone without one to
 * /login. That is correct for the web app and WRONG for /wix-dashboard: Wix
 * merchants authenticate with a Wix instance token through WixContext and
 * have no Firebase user at all, so wrapping that route in ProtectedRoute
 * would bounce every Wix merchant to a login page they cannot use.
 *
 * An MFA challenge, though, is orthogonal to how the caller authenticated -
 * the backend raises it for any identity it can resolve to a flagged account.
 * So the challenge redirect lives here, with no auth requirement, and
 * ProtectedRoute composes it rather than duplicating it.
 *
 * Rendering `children` unchanged when there is no challenge means this is
 * safe to wrap around anything.
 */

import { Navigate, useLocation } from 'react-router-dom';

import { useMfaChallenge } from '../../hooks/useMfaChallenge';

interface MfaChallengeGuardProps {
  children: React.ReactNode;
}

export default function MfaChallengeGuard({ children }: MfaChallengeGuardProps) {
  const challenge = useMfaChallenge();
  const location = useLocation();

  // Never redirect away from the enrollment page itself - that is the one
  // place the user can resolve the challenge, and the backend keeps
  // /api/v1/mfa exempt precisely so it stays reachable.
  //
  // The search string is carried across deliberately. A Wix merchant's only
  // credential is the `instance` query parameter, and it lives nowhere else -
  // getWixInstanceFromUrl() reads window.location.search and nothing persists
  // it. Redirecting to a bare pathname therefore stripped the merchant's
  // identity on the way to the one page that needs it, leaving enrolment
  // unable to authenticate even once the page itself was reachable.
  if (challenge && location.pathname !== challenge.setupUrl) {
    return (
      <Navigate
        to={{ pathname: challenge.setupUrl, search: location.search }}
        state={{ from: location, mfaMessage: challenge.message }}
        replace
      />
    );
  }

  return <>{children}</>;
}
